import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ChartNoAxesCombined, Check, Clock3 } from "lucide-react";

export default function OnlineRatingHistory({ athleteId, chessComUsername, lichessUsername }) {
  const [loaded, setLoaded] = useState(null);
  const [requestState, setRequestState] = useState("loading");
  const [range, setRange] = useState("12m");
  const [platform, setPlatform] = useState("chess_com");
  const [mode, setMode] = useState("bullet");
  const today = historyEndDate();
  const since = historyStartDate(range);
  const accountUsername = platform === "lichess" ? lichessUsername : chessComUsername;
  const requestKey = JSON.stringify([athleteId, platform, mode, range, (accountUsername || "").trim().toLowerCase(), since]);
  const history = loaded?.key === requestKey ? loaded.data : null;

  useEffect(() => {
    let active = true;
    setRequestState("loading");
    api.get(`/athletes/${athleteId}/rating-history`, {
      params: { platform, mode, since, profile_username: accountUsername?.trim() || "" },
    }).then(({ data }) => {
      if (!active) return;
      if ((data?.[platform]?.username || "").toLowerCase() !== (accountUsername || "").trim().toLowerCase()) {
        setLoaded({ key: requestKey, data: {} });
        setRequestState("error");
        return;
      }
      setLoaded({ key: requestKey, data });
      setRequestState(data.history_status || "ok");
    }).catch(() => {
      if (active) { setLoaded({ key: requestKey, data: {} }); setRequestState("error"); }
    });
    return () => { active = false; };
  }, [athleteId, accountUsername, platform, mode, range, since, requestKey]);

  const series = history?.[platform]?.modes?.[mode] || [];
  const validInRange = series.filter(point => isValidHistoryPoint(point)
    && (!since || point.date >= since) && point.date <= today);
  const points = dailyHistoryPoints(validInRange);
  const start = points[0], end = points[points.length - 1];
  const delta = start && end ? end.rating - start.rating : 0;
  const axisStart = since ? Date.parse(`${since}T00:00:00Z`) : points[0]?.timestamp;
  const axisEnd = Date.parse(`${today}T00:00:00Z`);
  const insufficientHistory = platform === "lichess" && points.length > 0
    && !hasSufficientTemporalCoverage(points, since, today);
  const emptyReason = insufficientHistory ? "insufficient" : "empty";
  const showChart = requestState === "ok" && points.length > 0 && !insufficientHistory;
  const platformName = platform === "lichess" ? "Lichess" : "Chess.com";
  const emptyDescription = platform === "lichess"
    ? "O Lichess não disponibilizou partidas classificadas desta modalidade."
    : `O ${platformName} não possui registros disponíveis para esta modalidade no período selecionado.`;

  return <div data-testid="online-rating-history">
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-3"><div className="rounded-xl border border-amber-300/20 bg-amber-300/[0.08] p-2.5 text-amber-200"><ChartNoAxesCombined className="h-5 w-5" /></div><div><div className="text-xs font-medium uppercase tracking-[0.16em] text-amber-200/80">Ratings online</div><h2 className="mt-1 text-lg font-semibold text-white sm:text-xl">Evolução de desempenho</h2></div></div>
      <div className="flex flex-wrap gap-2">
        <select aria-label="Plataforma" value={platform} onChange={event => setPlatform(event.target.value)} className="rounded-lg border border-white/10 bg-[#10294a] px-3 py-2.5 text-sm text-white shadow-sm outline-none transition hover:border-amber-200/30 focus:border-amber-300/60"><option value="chess_com">Chess.com</option><option value="lichess">Lichess</option></select>
        <select aria-label="Modalidade" value={mode} onChange={event => setMode(event.target.value)} className="rounded-lg border border-white/10 bg-[#10294a] px-3 py-2.5 text-sm text-white shadow-sm outline-none transition hover:border-amber-200/30 focus:border-amber-300/60">{["bullet", "blitz", "rapid"].map(value => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}</select>
        <select aria-label="Período" value={range} onChange={event => setRange(event.target.value)} className="rounded-lg border border-white/10 bg-[#10294a] px-3 py-2.5 text-sm text-white shadow-sm outline-none transition hover:border-amber-200/30 focus:border-amber-300/60"><option value="30d">30 dias</option><option value="3m">3 meses</option><option value="6m">6 meses</option><option value="12m">12 meses</option><option value="all">Todo o histórico</option></select>
      </div>
    </div>
    {showChart && start && end ? <>
      <div className="mb-5 grid grid-cols-3 gap-2 sm:gap-3"><Stat label="Rating inicial" value={start.rating} /><Stat label="Rating atual" value={end.rating} /><Stat label="Variação" value={`${delta > 0 ? "+" : ""}${delta}`} tone={delta >= 0 ? "text-emerald-300" : "text-rose-300"} /></div>
      <div className="rounded-2xl border border-amber-100/[0.07] bg-gradient-to-b from-[#0b1b30] to-[#061427] px-1 pb-3 pt-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.025),0_20px_45px_rgba(0,0,0,0.18)] sm:px-3"><div className="h-64 w-full sm:h-80"><ResponsiveContainer width="100%" height="100%"><LineChart data={points} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
        <CartesianGrid stroke="#dbeafe" strokeOpacity={0.055} vertical={false}/><XAxis type="number" dataKey="timestamp" domain={[axisStart, axisEnd]} tick={{ fill: "#91a4bd", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#ffffff18" }} minTickGap={34} tickMargin={12} tickFormatter={formatAxisDate}/><YAxis domain={["auto", "auto"]} tick={{ fill: "#91a4bd", fontSize: 10 }} tickLine={false} axisLine={false} width={42} tickMargin={8}/>
        <Tooltip content={<PremiumTooltip/>} cursor={{ stroke: "#fbbf24", strokeOpacity: 0.32, strokeDasharray: "4 5" }}/>
        <Line type="linear" dataKey="rating" name="Rating" stroke="#fbbf24" strokeWidth={2.75} dot={{ r: 2.2, fill: "#fcd34d", stroke: "#071426", strokeWidth: 1 }} activeDot={{ r: 5, strokeWidth: 2, stroke: "#071426", fill: "#fff1b8" }} isAnimationActive animationDuration={520} connectNulls={false}/>
      </LineChart></ResponsiveContainer></div></div>
      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-slate-500"><span>{since ? `${since} – ${today}` : `${start.date} – ${end.date}`}</span><span className="inline-flex items-center gap-1.5"><Clock3 className="h-3 w-3"/>Período selecionado</span></div>
    </> : <div data-empty-reason={requestState === "ok" ? emptyReason : undefined} className="rounded-xl border border-dashed border-white/10 bg-[#061427]/40 px-5 py-12 text-center"><div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.04] text-slate-400">{requestState === "ok" ? <Check className="h-4 w-4"/> : <ChartNoAxesCombined className="h-4 w-4"/>}</div><p className="text-sm font-medium text-slate-300">{requestState === "loading" ? "Carregando histórico de ratings…" : requestState === "error" ? "Não foi possível consultar o histórico." : requestState === "incomplete" ? "O histórico está incompleto." : "Ainda não há histórico para esta modalidade"}</p><p className="mt-1 text-xs text-slate-500">{requestState === "loading" ? "Os dados serão exibidos assim que a consulta terminar." : requestState === "error" ? `Não foi possível consultar o histórico do ${platformName}. Tente novamente mais tarde.` : requestState === "incomplete" ? `Não foi possível confirmar a cobertura completa do histórico do ${platformName} devido a uma interrupção ou limite da API.` : emptyDescription}</p></div>}
  </div>;
}

export function historyStartDate(range, now = new Date()) {
  if (range === "all") return undefined;
  const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (range === "30d") date.setUTCDate(date.getUTCDate() - 30);
  else {
    const months = { "3m": 3, "6m": 6, "12m": 12 }[range] || 12;
    const day = date.getUTCDate();
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() - months);
    const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
    date.setUTCDate(Math.min(day, lastDay));
  }
  return date.toISOString().slice(0, 10);
}

export function historyEndDate(now = new Date()) {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString().slice(0, 10);
}

export function isValidHistoryPoint(point) {
  if (!point || !/^\d{4}-\d{2}-\d{2}$/.test(point.date) || !Number.isFinite(Number(point.rating))) return false;
  const date = new Date(`${point.date}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === point.date;
}

export function dailyHistoryPoints(points) {
  const byDate = new Map();
  points.forEach(point => { if (isValidHistoryPoint(point)) byDate.set(point.date, point); });
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date))
    .map(point => ({ ...point, timestamp: Date.parse(`${point.date}T00:00:00Z`) }));
}

export function hasSufficientTemporalCoverage(points, since, until) {
  if (!points.length) return false;
  if (!since) return true;
  if (!until) return false;
  const selectedDuration = Date.parse(`${until}T00:00:00Z`) - Date.parse(`${since}T00:00:00Z`);
  const coveredDuration = Date.parse(`${points[points.length - 1].date}T00:00:00Z`)
    - Date.parse(`${points[0].date}T00:00:00Z`);
  return selectedDuration > 0 && coveredDuration / selectedDuration >= 0.5;
}

function formatDate(timestamp) {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function formatAxisDate(timestamp) {
  return new Intl.DateTimeFormat("pt-BR", {
    month: "short", year: "2-digit", timeZone: "UTC",
  }).format(new Date(timestamp)).replace(" de ", "/");
}

function PremiumTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const date = payload[0].payload.date || formatDate(label);
  return <div className="min-w-36 rounded-xl border border-amber-200/20 bg-[#071426]/95 px-3.5 py-3 shadow-[0_14px_36px_rgba(0,0,0,0.45)] backdrop-blur-md">
    <div className="flex items-center gap-2 text-[11px] font-medium text-slate-400"><span className="h-1.5 w-1.5 rounded-full bg-amber-300 shadow-[0_0_10px_rgba(251,191,36,0.8)]"/>{formatTooltipDate(date)}</div>
    <div className="mt-1.5 flex items-baseline justify-between gap-4"><span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Rating</span><span className="text-xl font-semibold tabular-nums text-amber-200">{payload[0].value}</span></div>
  </div>;
}

function formatTooltipDate(value) {
  const timestamp = typeof value === "number" ? value : Date.parse(`${value}T00:00:00Z`);
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit", month: "short", year: "numeric", timeZone: "UTC",
  }).format(new Date(timestamp));
}

function Stat({ label, value, tone = "text-white" }) {
  return <div className="rounded-xl border border-white/[0.08] bg-white/[0.025] p-3 sm:p-4"><div className="text-[10px] font-medium uppercase tracking-[0.15em] text-slate-400 sm:text-xs">{label}</div><div className={`mt-1.5 text-xl font-semibold tabular-nums sm:text-2xl ${tone}`}>{value}</div></div>;
}
